import {ChangeDetectionStrategy, Component, effect, ElementRef, HostBinding, inject, input, model, Renderer2} from '@angular/core';
import {TAILWIND_COLORS} from '../../tailwincss-colors';

// This component helps to use font-awesome icons in the application
// it resolves the issues of re-rendering of icons (svg based) in the application
// use case: need to render multiple icons inside button conditionally

@Component({
	selector: 'icon',
	standalone: true,
	imports: [],
	styles: `
		:host {
			@apply inline-flex items-center justify-center;
		}
	`,
	template: ``,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent {
	@HostBinding('style') style: Record<string, string> = {
		'--fa-primary-color': '',
		'--fa-secondary-color': '',
	};
	/* --------------------------------------------------  DOM -------------------------------------------------- */
	public readonly elementRef = inject(ElementRef<HTMLElement>);
	/* ------------------------------------------  Types and Constants ------------------------------------------ */
	/* ------------------------------------------------  Inputs ------------------------------------------------ */
	readonly icon = input.required<string>();
	readonly iconStyles = model<Record<string, string>>();
	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */
	private readonly renderer = inject(Renderer2);
	/* ------------------------------------------------  Outputs ------------------------------------------------ */
	private readonly FaDuotonePrimaryColorRegex = /fa-primary-((?:[a-f0-9]{6}|[a-z]+)(?:-[0-9]+)?|\[[#]?([a-f0-9]{3,6})\])/gi;

	/* -------------------------------------------------- Data -------------------------------------------------- */
	private readonly FaDuotoneSecondaryColorRegex = /fa-secondary-((?:[a-f0-9]{6}|[a-z]+)(?:-[0-9]+)?|\[[#]?([a-f0-9]{3,6})\])/gi;

	/* ------------------------------------------------  Signals ------------------------------------------------ */
	constructor() {
		effect(() => {
			const icon = this.icon();
			if (!icon) return;
			const {styles, updatedIconClass} = this.checkIconClass(icon);
			this.style = styles;
			this.setIconElement(this.elementRef.nativeElement, updatedIconClass, styles);
		});
	}

	/* ------------------------------------------------  Methods ------------------------------------------------ */
	private setIconElement(element: HTMLElement, iconClass: string, iconStyles: Record<string, string>): void {
		if (!element) return;
		// clear the element content
		element.innerHTML = '';
		const iconElement = this.renderer.createElement('i');
		this.renderer.setAttribute(iconElement, 'class', iconClass);
		for (const [key, value] of Object.entries(iconStyles)) {
			if (!value) continue;
			this.renderer.setStyle(iconElement, key, value);
		}
		this.renderer.appendChild(element, iconElement);
	}

	private checkIconClass(iconClass: string): {styles: Record<string, string>; updatedIconClass: string} {
		const iconStyles: Record<string, string> = {};
		let updatedIconClass = iconClass;
		// Extract all primary colors
		const primaryMatches = Array.from(iconClass.matchAll(this.FaDuotonePrimaryColorRegex));
		for (const match of primaryMatches) {
			const primaryColor = match[1];
			if (primaryColor) {
				// Check if it's a hex color in square brackets
				if (primaryColor.startsWith('[') && primaryColor.endsWith(']')) {
					const hexColor = match[2];
					if (hexColor) {
						iconStyles['--fa-primary-color'] = `#${hexColor.replace(/^#/, '')}`;
					}
				} else {
					const tailwindColor = this.getTailwindColor(primaryColor);
					iconStyles['--fa-primary-color'] = tailwindColor;
					updatedIconClass = updatedIconClass.replace(match[0], tailwindColor);
				}
			}
		}
		// Extract all secondary colors
		const secondaryMatches = Array.from(iconClass.matchAll(this.FaDuotoneSecondaryColorRegex));
		for (const match of secondaryMatches) {
			const secondaryColor = match[1];
			if (secondaryColor) {
				// Check if it's a hex color in square brackets
				if (secondaryColor.startsWith('[') && secondaryColor.endsWith(']')) {
					const hexColor = match[2];
					if (hexColor) {
						iconStyles['--fa-secondary-color'] = `#${hexColor.replace(/^#/, '')}`;
					}
				} else {
					const tailwindColor = this.getTailwindColor(secondaryColor);
					iconStyles['--fa-secondary-color'] = tailwindColor;
					updatedIconClass = updatedIconClass.replace(match[0], tailwindColor);
				}
			}
		}
		return {styles: iconStyles, updatedIconClass};
	}

	private getTailwindColor(color: string): string {
		return TAILWIND_COLORS[color];
	}
}
